import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { superAdminAPI } from '../../services/api';
import {
  ExportMenu, FilterSelect, ReportFilterBar, EMPTY_REPORT_FILTERS, filterReportRows, uniqueSorted,
  SLA_FILTER_OPTIONS, matchesSla, slaLabel,
} from '../../components/SuperAdmin/ListTools';
import { formatExportDate } from '../../utils/exportData';
import {
  HiOutlineReply,
  HiOutlineRefresh,
  HiOutlineTicket,
  HiOutlineCheckCircle,
  HiOutlineExclamationCircle,
} from 'react-icons/hi';
import { ReportGuide, periodLabel } from '../../components/SuperAdmin/ReportGuide';

const formatDateTime = (d) =>
  d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const PERIODS = [7, 30, 90, 180, 365];

const PRIORITY_LABELS = {
  urgent: 'Urgent',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const PRIORITY_COLORS = {
  urgent: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-blue-100 text-blue-700',
  low: 'bg-gray-100 text-gray-600',
};

const formatMinutes = (m) => {
  if (m == null) return '—';
  if (m < 60) return `${m}m`;
  if (m < 1440) return `${Math.round((m / 60) * 10) / 10}h`;
  return `${Math.round((m / 1440) * 10) / 10}d`;
};

const FirstResponseTimeAnalysis = () => {
  const [days, setDays] = useState(30);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['first-response-analysis', days],
    queryFn: () => superAdminAPI.getFirstResponseAnalysis({ days }),
    refetchInterval: 120000,
  });

  // Axios → res.data (server body) → body.data
  const body = data?.data?.data || {};
  const stats = body.stats || {};
  const byPriority = Array.isArray(body.byPriority) ? body.byPriority : [];
  const allRows = Array.isArray(body.rows) ? body.rows : [];
  const [filters, setFilters] = useState({ ...EMPTY_REPORT_FILTERS, sla: '' });
  const rows = filterReportRows(allRows, filters, {
    searchOf: (r) => [r.ticketNumber, r.subject, r.organizationName],
  }).filter((r) => matchesSla(r, filters.sla));

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex-1 basis-80">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <HiOutlineReply className="w-7 h-7 text-primary-500" />
            First Reply Time
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            How long customers wait for the first reply on a new ticket
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs font-medium text-gray-500">Show last</span>
          <div className="flex flex-wrap gap-1 bg-gray-100 rounded-xl p-1">
            {PERIODS.map((p) => (
              <button
                key={p}
                onClick={() => setDays(p)}
                className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  days === p ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {periodLabel(p)}
              </button>
            ))}
          </div>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium
                       hover:bg-primary-600 shadow-lg shadow-primary-500/25 disabled:opacity-50"
          >
            <HiOutlineRefresh className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <ReportGuide
        id="first-response"
        summary="Measures the wait between a customer raising a ticket and our team’s first reply. Lower is better."
        points={[
          ["Average", "The typical time across all tickets in the period."],
          ["Fastest / Slowest", "The quickest and the longest single ticket."],
          ["Tickets responded", "Tickets that got a reply in the period."],
          ["Replied on time", "% of first replies sent before the promised deadline."],
        ]}
      />

      {/* Stat tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Average Wait</p>
          <p className="text-3xl font-bold text-primary-500 mt-2">{formatMinutes(stats.avgMinutes)}</p>
          <p className="text-xs text-gray-400 mt-1">Across all responded tickets</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Fastest</p>
          <p className="text-3xl font-bold text-green-500 mt-2">{formatMinutes(stats.minMinutes)}</p>
          <p className="text-xs text-gray-400 mt-1">Quickest response</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Slowest</p>
          <p className="text-3xl font-bold text-red-500 mt-2">{formatMinutes(stats.maxMinutes)}</p>
          <p className="text-xs text-gray-400 mt-1">Longest response wait</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Tickets Responded</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{stats.totalResponded ?? 0}</p>
          <p className="text-xs text-gray-400 mt-1">Last {body.windowDays ?? days} days</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Replied on Time</p>
          <p className={`text-3xl font-bold mt-2 ${
            (stats.slaMetRate ?? 0) >= 70 ? 'text-green-500'
            : (stats.slaMetRate ?? 0) >= 50 ? 'text-amber-500'
            : 'text-red-500'
          }`}>
            {stats.slaMetRate ?? 0}%
          </p>
          <p className="text-xs text-gray-400 mt-1">{stats.slaBreachedCount ?? 0} missed the deadline</p>
        </div>
      </div>

      {/* By priority breakdown */}
      {byPriority.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h2 className="text-sm font-semibold text-gray-700">First Response Time by Priority</h2>
            <ExportMenu
              fileName={`first-response-by-priority-${days}d`}
              getRows={() => byPriority}
              columns={[
                { header: 'Priority', value: (p) => PRIORITY_LABELS[p.priority] || p.priority },
                { header: 'Responded', value: (p) => p.count },
                { header: 'Avg', value: (p) => formatMinutes(p.avgMinutes) },
                { header: 'Min', value: (p) => formatMinutes(p.minMinutes) },
                { header: 'Max', value: (p) => formatMinutes(p.maxMinutes) },
              ]}
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">Priority</th>
                  <th className="px-4 py-3">Responded</th>
                  <th className="px-4 py-3">Average</th>
                  <th className="px-4 py-3">Fastest</th>
                  <th className="px-4 py-3">Slowest</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {byPriority.map((p) => (
                  <tr key={p.priority} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${PRIORITY_COLORS[p.priority] || 'bg-gray-100 text-gray-600'}`}>
                        {PRIORITY_LABELS[p.priority] || p.priority}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{p.count}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{formatMinutes(p.avgMinutes)}</td>
                    <td className="px-4 py-3 text-green-600">{formatMinutes(p.minMinutes)}</td>
                    <td className="px-4 py-3 text-red-600">{formatMinutes(p.maxMinutes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Responded tickets table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-700">Ticket First Responses</h2>
          <span className="text-xs text-gray-400">
            {rows.length === allRows.length ? `${rows.length} shown` : `${rows.length} of ${allRows.length} shown`}
          </span>
        </div>

        {allRows.length > 0 && (
          <ReportFilterBar
            filters={filters}
            onChange={setFilters}
            searchPlaceholder="Search ticket #, subject, organization…"
            organizations={uniqueSorted(allRows.map((r) => r.organizationName))}
            extraActive={!!filters.sla}
            onResetExtra={() => setFilters((f) => ({ ...f, sla: '' }))}
            exportProps={{
              fileName: `first-responses-${days}d`,
              getRows: () => rows,
              columns: [
                { header: 'Ticket #', value: (r) => r.ticketNumber },
                { header: 'Subject', value: (r) => r.subject },
                { header: 'Organization', value: (r) => r.organizationName },
                { header: 'Priority', value: (r) => PRIORITY_LABELS[r.priority] || r.priority },
                { header: 'Created', value: (r) => formatExportDate(r.createdAt) },
                { header: 'First Response', value: (r) => formatExportDate(r.firstResponseAt) },
                { header: 'Response Time (min)', value: (r) => r.responseMinutes },
                { header: 'Response Time', value: (r) => formatMinutes(r.responseMinutes) },
                { header: 'SLA', value: (r) => slaLabel(r.slaBreached) },
              ],
            }}
          >
            <FilterSelect
              value={filters.sla}
              onChange={(v) => setFilters((f) => ({ ...f, sla: v }))}
              allLabel="SLA: Any"
              options={SLA_FILTER_OPTIONS}
            />
          </ReportFilterBar>
        )}

        {isLoading ? (
          <div className="p-8 text-center text-gray-400 text-sm">Loading first response analysis…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-400">
            {allRows.length > 0 ? 'No tickets match these filters' : 'No tickets received a first response in this period'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">Ticket</th>
                  <th className="px-4 py-3">Organization</th>
                  <th className="px-4 py-3">Priority</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3">First Response</th>
                  <th className="px-4 py-3">Response Time</th>
                  <th className="px-4 py-3">On Time?</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row) => (
                  <tr key={row.ticketId || row.ticketNumber} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <HiOutlineTicket className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-gray-900">{row.ticketNumber}</p>
                          <p className="text-xs text-gray-400 max-w-[240px] truncate" title={row.subject}>
                            {row.subject}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{row.organizationName || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${PRIORITY_COLORS[row.priority] || 'bg-gray-100 text-gray-600'}`}>
                        {PRIORITY_LABELS[row.priority] || row.priority}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{formatDateTime(row.createdAt)}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDateTime(row.firstResponseAt)}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{formatMinutes(row.responseMinutes)}</td>
                    <td className="px-4 py-3">
                      {row.slaBreached === null ? (
                        <span className="text-xs text-gray-400">—</span>
                      ) : row.slaBreached ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600">
                          <HiOutlineExclamationCircle className="w-4 h-4" />
                          Breached
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600">
                          <HiOutlineCheckCircle className="w-4 h-4" />
                          Met
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default FirstResponseTimeAnalysis;